import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API } from '../../config/api';
import { Play, DollarSign, Users, TrendingUp, Loader2, ChevronDown, Music2 } from 'lucide-react';
import { WalletsPanel } from './WalletsPanel';

const formatMoney = (v) => `$${(Number(v) || 0).toFixed(2)}`;
const formatDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }); }
  catch { return '—'; }
};

export function FeaturedAnalyticsPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [trackBreakdowns, setTrackBreakdowns] = useState({});
  const [sortKey, setSortKey] = useState('gross');

  const api = axios.create({ withCredentials: true });

  const fetchAnalytics = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`${API}/featured-artists/admin/analytics`);
      setData(data);
    } catch (e) {
      console.error('Failed to fetch featured analytics', e);
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { fetchAnalytics(); }, [fetchAnalytics]);

  const loadTracks = async (artistId) => {
    if (trackBreakdowns[artistId]) return;
    try {
      const { data } = await api.get(`${API}/featured-artists/admin/analytics/${artistId}/tracks`);
      setTrackBreakdowns((prev) => ({ ...prev, [artistId]: data.tracks || [] }));
    } catch (e) {
      console.error('Failed to fetch track breakdown', e);
    }
  };

  const toggleExpand = (artistId) => {
    if (expandedId === artistId) {
      setExpandedId(null);
    } else {
      setExpandedId(artistId);
      loadTracks(artistId);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20" data-testid="featured-analytics-loading">
        <Loader2 className="w-8 h-8 animate-spin text-[#DAA520]" />
      </div>
    );
  }

  if (!data) return <p className="text-[#A3A3A3] p-6">Failed to load analytics.</p>;

  const rows = [...(data.rows || [])].sort((a, b) => {
    if (sortKey === 'plays') return b.plays - a.plays;
    if (sortKey === 'unlocks') return b.unlocks - a.unlocks;
    if (sortKey === 'gross') return b.gross - a.gross;
    if (sortKey === 'balance') return b.wallet_balance - a.wallet_balance;
    return 0;
  });

  const { totals } = data;
  const hasAnyData = totals.plays > 0 || totals.unlocks > 0;

  return (
    <div className="space-y-6" data-testid="featured-analytics-content">
      <div>
        <h2 className="font-cabinet text-2xl font-black uppercase tracking-tighter">FEATURED ARTIST ANALYTICS</h2>
        <p className="text-[#A3A3A3] text-sm mt-1">Plays, unlocks &amp; revenue across all {totals.artist_count} featured artist{totals.artist_count === 1 ? '' : 's'}.</p>
      </div>

      {/* Totals row */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="featured-totals">
        <StatCard icon={<Users className="w-5 h-5" />} value={totals.artist_count} label="Artists" />
        <StatCard icon={<Play className="w-5 h-5" />} value={totals.plays.toLocaleString()} label="Total Plays" accent="#DAA520" />
        <StatCard icon={<TrendingUp className="w-5 h-5" />} value={totals.unlocks.toLocaleString()} label="Total Unlocks" accent="#22C55E" />
        <StatCard icon={<DollarSign className="w-5 h-5" />} value={formatMoney(totals.gross)} label="Total Revenue" accent="#DAA520" />
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
        <StatCard small label="Artist Cut (paid out + balance)" value={formatMoney(totals.artist_cut)} />
        <StatCard small label="House Cut (your earnings)" value={formatMoney(totals.house_cut)} accent="#DAA520" />
        <StatCard small label="Avg Revenue / Unlock" value={totals.unlocks ? formatMoney(totals.gross / totals.unlocks) : '—'} />
      </div>

      {/* Sort buttons */}
      <div className="flex items-center gap-2 flex-wrap" data-testid="sort-buttons">
        <span className="text-[10px] uppercase tracking-wider text-[#A3A3A3] font-bold mr-1">Sort:</span>
        {[
          ['gross', 'Revenue'],
          ['plays', 'Plays'],
          ['unlocks', 'Unlocks'],
          ['balance', 'Wallet Balance'],
        ].map(([k, label]) => (
          <button key={k} onClick={() => setSortKey(k)}
            className={`px-3 py-1 text-[11px] uppercase tracking-wider font-bold border transition-colors ${
              sortKey === k
                ? 'bg-[#DAA520] border-[#DAA520] text-black'
                : 'bg-transparent border-[#262626] text-[#A3A3A3] hover:border-[#DAA520] hover:text-white'
            }`}
            data-testid={`sort-${k}`}>
            {label}
          </button>
        ))}
      </div>

      {/* Per-artist table */}
      <div className="bg-[#141414] border border-[#262626] overflow-hidden">
        {!hasAnyData ? (
          <div className="p-8 text-center text-[#A3A3A3] text-sm" data-testid="no-data">
            <Music2 className="w-10 h-10 mx-auto mb-2 opacity-30" />
            No plays or sales yet. Once fans listen or unlock featured tracks, you&apos;ll see live numbers here.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="featured-table">
              <thead className="bg-[#0A0A0A] border-b border-[#262626]">
                <tr className="text-[10px] uppercase tracking-wider text-[#A3A3A3]">
                  <th className="px-3 py-3 text-left">Artist</th>
                  <th className="px-3 py-3 text-right">Plays</th>
                  <th className="px-3 py-3 text-right">Unlocks</th>
                  <th className="px-3 py-3 text-right">Revenue</th>
                  <th className="px-3 py-3 text-right">Artist Cut</th>
                  <th className="px-3 py-3 text-right">House Cut</th>
                  <th className="px-3 py-3 text-right">Wallet</th>
                  <th className="px-3 py-3 text-right">Last Sale</th>
                  <th className="px-3 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <RowFragment
                    key={r.artist_id}
                    row={r}
                    expanded={expandedId === r.artist_id}
                    onToggle={() => toggleExpand(r.artist_id)}
                    tracks={trackBreakdowns[r.artist_id]}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ── Wallets / Payouts (moved from sidebar, anchored below analytics) ── */}
      <div className="border-t-2 border-[#262626] pt-8 mt-12" data-testid="embedded-wallets">
        <WalletsPanel />
      </div>
    </div>
  );
}

function StatCard({ icon, value, label, accent = '#FF3B30', small = false }) {
  if (small) {
    return (
      <div className="bg-[#141414] border border-[#262626] p-4">
        <p className="text-[10px] uppercase tracking-wider text-[#A3A3A3] font-bold">{label}</p>
        <p className="font-mono text-xl font-bold mt-1" style={{ color: accent }}>{value}</p>
      </div>
    );
  }
  return (
    <div className="stat-card">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 flex items-center justify-center" style={{ background: accent }}>{icon}</div>
        <div>
          <p className="stat-value">{value}</p>
          <p className="stat-label">{label}</p>
        </div>
      </div>
    </div>
  );
}

function RowFragment({ row, expanded, onToggle, tracks }) {
  return (
    <>
      <tr className="border-b border-[#262626] hover:bg-[#0A0A0A] transition-colors" data-testid={`row-${row.slug || row.artist_id}`}>
        <td className="px-3 py-3">
          <div className="flex flex-col">
            <span className="font-bold text-white">{row.artist_name}</span>
            <span className="text-[10px] text-[#A3A3A3] uppercase tracking-wider">
              {row.status} · {row.split_pct}% split
            </span>
          </div>
        </td>
        <td className="px-3 py-3 text-right font-mono">{row.plays.toLocaleString()}</td>
        <td className="px-3 py-3 text-right font-mono">{row.unlocks.toLocaleString()}</td>
        <td className="px-3 py-3 text-right font-mono text-[#DAA520]">{formatMoney(row.gross)}</td>
        <td className="px-3 py-3 text-right font-mono text-[#22C55E]">{formatMoney(row.artist_cut)}</td>
        <td className="px-3 py-3 text-right font-mono">{formatMoney(row.house_cut)}</td>
        <td className="px-3 py-3 text-right">
          <div className="flex flex-col items-end">
            <span className="font-mono text-[#DAA520]">{formatMoney(row.wallet_balance)}</span>
            {row.wallet_paid > 0 && <span className="text-[10px] text-[#A3A3A3]">Paid: {formatMoney(row.wallet_paid)}</span>}
          </div>
        </td>
        <td className="px-3 py-3 text-right text-[#A3A3A3] text-xs">{formatDate(row.last_sale_at)}</td>
        <td className="px-3 py-3 text-right">
          <button onClick={onToggle}
            className="p-1 hover:text-[#DAA520] transition-colors"
            data-testid={`expand-${row.artist_id}`}>
            <ChevronDown className={`w-4 h-4 transition-transform ${expanded ? 'rotate-180' : ''}`} />
          </button>
        </td>
      </tr>
      {expanded && (
        <tr className="bg-[#0A0A0A]">
          <td colSpan="9" className="px-3 py-4">
            <div className="pl-4 border-l-2 border-l-[#DAA520]">
              <p className="text-[10px] uppercase tracking-wider text-[#DAA520] font-bold mb-2">PER-TRACK BREAKDOWN</p>
              {!tracks ? (
                <p className="text-xs text-[#A3A3A3] italic">Loading…</p>
              ) : tracks.length === 0 ? (
                <p className="text-xs text-[#A3A3A3] italic">No tracks yet for this artist.</p>
              ) : (
                <table className="w-full text-xs">
                  <thead>
                    <tr className="text-[10px] uppercase text-[#A3A3A3]">
                      <th className="text-left py-1">Track</th>
                      <th className="text-right py-1">Plays</th>
                      <th className="text-right py-1">Unlocks</th>
                      <th className="text-right py-1">Price</th>
                      <th className="text-right py-1">Revenue</th>
                      <th className="text-right py-1">Artist Cut</th>
                    </tr>
                  </thead>
                  <tbody>
                    {tracks.map((t) => (
                      <tr key={t.track_id} className="border-t border-[#262626]/60">
                        <td className="py-1.5 truncate max-w-xs">{t.title}</td>
                        <td className="text-right py-1.5 font-mono">{t.plays.toLocaleString()}</td>
                        <td className="text-right py-1.5 font-mono">{t.unlocks}</td>
                        <td className="text-right py-1.5 font-mono text-[#A3A3A3]">{t.unlock_price ? formatMoney(t.unlock_price) : '—'}</td>
                        <td className="text-right py-1.5 font-mono text-[#DAA520]">{formatMoney(t.gross)}</td>
                        <td className="text-right py-1.5 font-mono text-[#22C55E]">{formatMoney(t.artist_cut)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </td>
        </tr>
      )}
    </>
  );
}
